import { useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { ChevronLeft, Plus, X } from "lucide-react";
import { toast } from "react-toastify";
import { setTitle } from "../../../helpers/MetaTag";
import { resetStep } from "../../../redux/slice/stepSlice";
import { clearSketch } from "../../../redux/slice/sketchSlice";
import Button from "../../../components/ui/Button";
import BottomBar from "../../../components/ui/BottomBar";
import WizardShell from "../../../components/ui/WizardShell";
import ErrorState from "../../../components/ui/ErrorState";
import Skeleton, { SkeletonGroup } from "../../../components/ui/Skeleton";
import { ctaFor, mixStatus, optionForPlan, optionsFor, summaryRows } from "../../../components/gift/giftPlans";
import { getGift, giftIdFromSearch, giftPagePath, giftWelcomePath, newGiftBookPath, setGiftBookPlan } from "../../../services/giftService";
import { classNames } from "../../../helpers/classNames";

const COLORS = ["#EBC9B3", "#C9D7DE", "#D6E0C8", "#E8D5E0", "#F0DDB8", "#CFE0DA", "#DCCFE8", "#E6C9C0", "#C8D2E6", "#E0E3C3"];

/**
 * /gift/plan?id=... Gift 2 (docs/design/HANDOFF.md: GiftPlanTwo, GiftPlan,
 * GiftPlanMany): how to split the paid copies before any book exists. Two
 * and three copies get fixed choices; four or more add "Mix and match" with
 * steppers. Saved to the API as copies per book (the slots plan); started
 * books keep their slot.
 */
const GiftPlan = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const giftId = giftIdFromSearch(location.search, "id");
  const [gift, setGift] = useState(null);
  const [error, setError] = useState(null);
  const [choice, setChoice] = useState(null);
  const [mix, setMix] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setTitle("Split your books | MemoryDoodle");
  }, []);

  const load = async () => {
    if (!giftId) return setError("We couldn't find your gift.");
    setError(null);
    const res = await getGift(giftId);
    if (!res?.success) return setError(res?.message || "We couldn't load your gift.");
    const g = res.data;
    setGift(g);
    const options = optionsFor(g.quantity);
    const saved = optionForPlan(options, g.plan);
    const fallback = options.find((o) => o.id === "split") || options.find((o) => o.id === "two") || options.find((o) => o.id === "each") || options[0];
    setChoice((saved || fallback).id);
    setMix(Array.isArray(g.plan) && g.plan.length ? g.plan : g.quantity >= 4 ? [2, 2, 1].slice(0, Math.max(1, Math.min(3, g.quantity - 1))) : []);
  };
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [giftId]);

  const n = gift?.quantity || 0;
  const options = useMemo(() => optionsFor(n), [n]);
  const current = options.find((o) => o.id === choice) || options[0];
  const isMix = current?.id === "mix";
  const split = isMix ? mix : current?.split || [];
  const { assigned, left, full } = mixStatus(split, n);
  const ready = !isMix || left === 0;
  const hasBooks = (gift?.books || []).length > 0;
  const startedCount = (gift?.books || []).length;

  const save = async () => {
    if (!gift || !ready || saving) return;
    if (split.length < startedCount) {
      toast.error(`You've already started ${startedCount} ${startedCount === 1 ? "book" : "books"}, so choose a split with at least ${startedCount}.`);
      return;
    }
    setSaving(true);
    const res = await setGiftBookPlan(gift.giftId, split);
    setSaving(false);
    if (!res?.success) {
      toast.error(res?.message || "Something went wrong. Please try again.");
      return;
    }
    if (hasBooks) {
      navigate(giftPagePath(gift.giftId));
      return;
    }
    dispatch(resetStep());
    dispatch(clearSketch());
    navigate(newGiftBookPath(gift.giftId));
  };

  if (error) {
    return (
      <WizardShell plain>
        <ErrorState title="We couldn't open your gift" onRetry={load}>
          {error}
        </ErrorState>
      </WizardShell>
    );
  }
  if (!gift) {
    return (
      <WizardShell plain>
        <SkeletonGroup label="Loading your gift" className="flex flex-col gap-4">
          <Skeleton shape="line" className="h-9 w-2/3" />
          <Skeleton shape="block" className="h-24" />
          <Skeleton shape="block" className="h-24" />
        </SkeletonGroup>
      </WizardShell>
    );
  }

  const cta = ctaFor(split, hasBooks);
  const primary = (
    <Button size="lg" fullWidth onClick={save} busy={saving} disabled={!ready}>
      {saving ? "Saving" : cta}
    </Button>
  );
  const note = !ready ? `Add ${left} more ${left === 1 ? "copy" : "copies"} to continue.` : null;

  return (
    <WizardShell plain bottomBar={<BottomBar primary={primary} note={note} className="md:hidden" />}>
      <div className="flex flex-col gap-4 lg:mx-auto lg:max-w-[936px] lg:gap-6">
        <Button variant="text" to={hasBooks ? giftPagePath(gift.giftId) : giftWelcomePath(gift.giftId)} icon={<ChevronLeft className="h-[18px] w-[18px]" aria-hidden="true" />} className="hidden self-start !px-0 lg:inline-flex">
          Back
        </Button>
        <div className="flex flex-col gap-1.5">
          <h1 className="m-0 font-heading text-[30px] font-normal leading-[1.1] text-md-dark md:text-[40px] lg:text-[44px]">Your gift includes {n} books</h1>
          <p className="m-0 text-[15px] leading-snug text-md-muted md:text-[17px]">
            {n === 2 ? "Make one book twice, or two different books." : n === 3 ? "Make copies of one book, different books, or a mix." : "Make copies of one book, a different book for each, or mix it up."}
          </p>
        </div>

        <div role="radiogroup" aria-label={`How to use your ${n} books`} className="flex flex-col gap-2.5">
          {options.map((o) => {
            const on = o.id === choice;
            return (
              <label
                key={o.id}
                className={classNames(
                  "flex cursor-pointer items-center gap-3.5 rounded-[14px] p-3.5 md:border-0 md:bg-transparent md:p-1",
                  on ? "border-2 border-md-pink bg-white" : "border border-md-pink-light-3 bg-[#FBF9F5]"
                )}
              >
                <input type="radio" name="gift-plan" checked={on} onChange={() => setChoice(o.id)} className="h-[22px] w-[22px] flex-shrink-0 border-md-control-input text-md-pink-dark focus:ring-md-pink" style={{ accentColor: "#9E4D5F" }} />
                <span className="flex flex-1 flex-col gap-0.5">
                  <span className="text-base font-bold text-md-dark">{o.title}</span>
                  <span className="text-sm leading-snug text-md-muted">{o.desc}</span>
                </span>
                {o.split ? (
                  // At most three little books, then "+N": five swatches would squeeze the words on a phone.
                  <span aria-hidden="true" className="flex flex-shrink-0 items-end gap-1.5 md:hidden">
                    {o.split.slice(0, 3).map((copies, i) => (
                      <span key={i} className="flex flex-col items-center gap-0.5">
                        <span className="h-9 w-7 rounded-[3px]" style={{ background: COLORS[i], boxShadow: copies > 1 ? "3px -3px 0 #FFFFFF, 4px -4px 0 #D9CFC4" : "none" }} />
                        <span className="text-[11px] font-semibold text-md-muted">×{copies}</span>
                      </span>
                    ))}
                    {o.split.length > 3 ? <span className="pb-4 text-xs font-bold text-md-muted">+{o.split.length - 3}</span> : null}
                  </span>
                ) : null}
              </label>
            );
          })}
        </div>

        <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:gap-6">
        <section aria-labelledby="gift-split-summary" className="flex flex-col gap-3 rounded-[14px] border border-md-pink-light-1 bg-white p-4 lg:min-w-0 lg:flex-1 lg:p-6">
          <div className="flex items-center justify-between gap-3">
            <h2 id="gift-split-summary" className="m-0 text-[15px] font-bold text-md-dark">
              You'll make {split.length} {split.length === 1 ? "book" : "books"}
            </h2>
            {isMix ? (
              <span role="status" className={classNames("rounded-full px-2.5 py-1 text-xs font-bold", full ? "bg-green-100 text-green-800" : "bg-amber-100 text-amber-800")}>
                {assigned} of {n} copies{left > 0 ? ` · ${left} left` : ""}
              </span>
            ) : null}
          </div>
          <div className="flex flex-col gap-2">
            {split.map((copies, i) => (
              <div key={i} className="flex items-center gap-3">
                <span aria-hidden="true" className="h-9 w-7 flex-shrink-0 rounded-[3px]" style={{ background: COLORS[i % COLORS.length] }} />
                <span className="flex-1 text-[15px] font-semibold text-md-dark">{summaryRows(split)[i].name}</span>
                {isMix ? (
                  <>
                    <div className="flex items-center overflow-hidden rounded-xl border border-md-control bg-white">
                      <button
                        type="button"
                        aria-label={`One less copy of Book ${i + 1}`}
                        disabled={copies <= 1}
                        onClick={() => setMix(mix.map((c, j) => (j === i ? c - 1 : c)))}
                        className="h-11 w-11 text-[22px] font-medium leading-none text-md-dark disabled:text-md-control-input"
                      >
                        −
                      </button>
                      <span className="w-8 text-center text-base font-bold text-md-dark">{copies}</span>
                      <button
                        type="button"
                        aria-label={`One more copy of Book ${i + 1}`}
                        disabled={full}
                        onClick={() => setMix(mix.map((c, j) => (j === i ? c + 1 : c)))}
                        className="h-11 w-11 text-[22px] font-medium leading-none text-md-dark disabled:text-md-control-input"
                      >
                        +
                      </button>
                    </div>
                    <button
                      type="button"
                      aria-label={`Remove Book ${i + 1}`}
                      disabled={mix.length <= 1}
                      onClick={() => setMix(mix.filter((_, j) => j !== i))}
                      className="flex h-11 w-11 items-center justify-center rounded-md text-md-muted disabled:text-md-control-input"
                    >
                      <X className="h-5 w-5" aria-hidden="true" />
                    </button>
                  </>
                ) : (
                  <span className="text-[15px] text-md-muted">{summaryRows(split)[i].copies}</span>
                )}
              </div>
            ))}
          </div>
          {isMix ? (
            <button
              type="button"
              onClick={() => setMix([...mix, 1])}
              disabled={full}
              className="flex min-h-[48px] items-center justify-center gap-2 rounded-xl border-2 border-dashed border-[#C9BCB0] text-[15px] font-semibold text-md-pink-dark disabled:border-md-pink-light-3 disabled:text-[#A39A94]"
            >
              <Plus className="h-5 w-5" aria-hidden="true" />
              Add another book
            </button>
          ) : null}
          <span className="text-[13px] leading-snug text-md-muted">Each book saves to My Books, so you can make them one at a time.</span>
        </section>

        <div className="hidden md:block lg:w-[260px] lg:flex-shrink-0">
          {primary}
          {note ? <p className="mt-2 text-center text-[13px] text-md-muted">{note}</p> : null}
        </div>
        </div>
      </div>
    </WizardShell>
  );
};

export default GiftPlan;
